import { decimalCompare, decimalSubtract, decimalSum, type DecimalValue } from "../../values/decimal";
import { formatMoney } from "../../values/number-format";
import { orderBalance, orderValue, roundMoney } from "../../orders/order-money";
import { countNote, DASHBOARD_COPY } from "../dashboard-copy";
import { coverageOf, stat, type Coverage } from "./build";
import type { DashboardWidgetData } from "../dashboard-data";

export type PricedOrder = { quantity: DecimalValue; pricePerItem?: number | string | null; paidTotal?: DecimalValue | null };

export function missingPriceNote(count: number): string | null {
  return count === 0 ? null : countNote(DASHBOARD_COPY.notes.missingPrice, count);
}

export function priceCoverage(missing: number, notes: Array<string | null> = []): Coverage {
  const coverage = coverageOf(missing === 0, [missingPriceNote(missing), ...notes]);
  return missing > 0 ? { ...coverage, missingPriceCount: missing } : coverage;
}

export function valueStat(orders: readonly PricedOrder[]): DashboardWidgetData {
  const values = orders.map((order) => orderValue(order.pricePerItem, order.quantity));
  const priced = values.filter((value): value is DecimalValue => value != null);
  const missing = values.length - priced.length;
  return stat(roundMoney(decimalSum(priced)), priceCoverage(missing));
}

function positiveOverpayment(value: DecimalValue): string {
  return decimalSubtract(0, value);
}

function overpaidNote(overpaid: readonly DecimalValue[]): string | null {
  if (overpaid.length === 0) return null;
  const total = formatMoney(roundMoney(decimalSum(overpaid.map(positiveOverpayment))));
  return countNote(DASHBOARD_COPY.notes.overpaid, overpaid.length, { total });
}

function balanceCoverage(missing: number, overpaid: readonly DecimalValue[]): Coverage {
  return priceCoverage(missing, [overpaidNote(overpaid)]);
}

export function balanceStat(orders: readonly PricedOrder[]): DashboardWidgetData {
  const balances = orders.map((order) => orderBalance(order.pricePerItem, order.quantity, order.paidTotal ?? 0));
  const known = balances.filter((balance): balance is DecimalValue => balance != null);
  const owed = known.filter((balance) => decimalCompare(balance, 0) > 0);
  const overpaid = known.filter((balance) => decimalCompare(balance, 0) < 0);
  return stat(roundMoney(decimalSum(owed)), balanceCoverage(balances.length - known.length, overpaid));
}
